import { Button, Heading, LuIcon, Text, toast } from "@overmap-ai/blocks"
import { selectCurrentUser } from "@overmap-ai/core"
import React, { type ChangeEventHandler, memo, useCallback, useRef } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { openChangePassword, sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { UserIcon } from "../Icons/UserIcon"

const maxProfilePictureSize = 10 * 1000 * 1000 // 10 MB

interface ProfileDetailsProps {
	title: string
	info: string
}

const ProfileDetails = (props: ProfileDetailsProps) => {
	return (
		<div className="flex flex-col">
			<Heading size="xs">{props.title}</Heading>
			<Text>{props.info}</Text>
		</div>
	)
}

interface PfpUploadTileProps {
	onClick: React.MouseEventHandler<HTMLButtonElement>
	onChange: React.ChangeEventHandler<HTMLInputElement>
	inputRef: React.LegacyRef<HTMLInputElement>
	hasProfilePicture: boolean
}

const ProfilePictureUploadControl = memo((props: PfpUploadTileProps) => {
	const { onClick, inputRef, onChange, hasProfilePicture } = props
	const user = useAppSelector(selectCurrentUser)
	const label = (hasProfilePicture ? "Replace" : "Choose a") + " profile picture"
	return (
		<>
			<UserIcon onClick={onClick} userId={user?.id ?? -1} size="xl" />
			<input className="hidden" ref={inputRef} type="file" name={label} aria-label={label} onChange={onChange} />
		</>
	)
})

ProfilePictureUploadControl.displayName = "ProfilePictureUploadControl"

export const ProfileSettings = () => {
	const user = useAppSelector(selectCurrentUser)

	const hiddenFileInput = useRef<HTMLInputElement>(null)
	const handleUploadClick = useCallback(() => {
		hiddenFileInput.current?.click()
	}, [])

	const handleUploadImage = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
		const { files } = e.target

		if (!files || files.length === 0) return

		const uploadedPFP: File = files[0]!

		if (uploadedPFP.type.split("/")[0] !== "image") {
			toast.error({
				title: "Please upload an image.",
				description: "",
			})
			return
		}
		if (uploadedPFP.size > maxProfilePictureSize) {
			toast.error({
				title: "Image must be smaller than 10MB",
				description: "",
			})
			return
		}
		void sdk.main.replaceProfilePicture(uploadedPFP)
	}, [])

	// The password is changed on the API's own page, which brings the user back here afterwards.
	const handleChangePassword = useCallback(() => openChangePassword(), [])

	// This is false if the organization's identity provider manages the password.
	// (Users that were stored before the API sent this field do not have it.)
	const canChangePassword = user?.can_change_password !== false
	const ssoProviderName = user?.sso_provider_name

	const hasProfilePicture = !!user?.profile.object_url && !!user?.profile.file_sha1 && !!user?.profile.file_extension

	return (
		<div className="flex w-full justify-between gap-2">
			<div className="flex flex-col gap-2 md:flex-row">
				<ProfilePictureUploadControl
					onClick={handleUploadClick}
					onChange={handleUploadImage}
					inputRef={hiddenFileInput}
					hasProfilePicture={hasProfilePicture}
				/>
				<ProfileDetails title="Username" info={user!.username} />
				<ProfileDetails title="Email" info={user!.email} />
			</div>
			{canChangePassword ? (
				<Button className="w-max" variant="soft" accentColor="base" size="sm" onClick={handleChangePassword}>
					<LuIcon icon={modelIconMapping.lock} />
					Change password
				</Button>
			) : (
				ssoProviderName && (
					<Text size="sm" accentColor="base">
						Because you sign in using {ssoProviderName}, you cannot change your password here.
					</Text>
				)
			)}
		</div>
	)
}
